import { memo } from 'react';
import { Autocomplete, TextField, Checkbox, FormControlLabel, Box } from '@mui/material';
import { FieldValue } from '../../../../../types/QueryBuilder';
import { useLanguage } from '../../../../../context/LanguageContext';
import ChipsSelectEditor from './ChipsSelectEditor';
import { setSelected } from './valueUtils';

interface SelectEditorProps {
    ruleId: string;
    value: string | string[];
    values: FieldValue[];
    isMultiSelect: boolean;
    compact: boolean;
    onChange: (value: string | string[]) => void;
}

const NO_SELECTION: string[] = [];

const SelectEditor = memo(function SelectEditor({ ruleId, value, values, isMultiSelect, compact, onChange }: SelectEditorProps) {
    const { t } = useLanguage();

    if (isMultiSelect) {
        const selectedValues = Array.isArray(value) ? value : NO_SELECTION;

        if (!compact) {
            return <ChipsSelectEditor ruleId={ruleId} values={values} selectedValues={selectedValues} onChange={onChange} />;
        }

        return (
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                    gridAutoFlow: { xs: 'row', sm: 'column' },
                    gridTemplateRows: { xs: 'none', sm: `repeat(${Math.ceil(values.length / 2)}, auto)` },
                    columnGap: 1,
                }}
            >
                {values.map(option => (
                    <FormControlLabel
                        key={option.name}
                        sx={{ m: 0 }}
                        control={
                            <Checkbox
                                checked={selectedValues.includes(option.name)}
                                onChange={(e) => onChange(setSelected(selectedValues, [option.name], e.target.checked))}
                                size="small"
                            />
                        }
                        label={option.label}
                    />
                ))}
            </Box>
        );
    }

    return (
        <Autocomplete
            fullWidth
            size="small"
            id={`${ruleId}-select`}
            options={values}
            value={values.find(option => option.name === value) ?? null}
            onChange={(_event, newValue) => onChange(newValue ? newValue.name : '')}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, val) => option.name === val.name}
            renderInput={(params) => (
                <TextField
                    {...params}
                    placeholder={t('pages.clients.queryBuilder.selectPlaceholder')}
                    inputProps={{ ...params.inputProps, autoComplete: 'off' }}
                />
            )}
            ListboxProps={{ style: { maxHeight: 300 } }}
            noOptionsText={t('pages.clients.queryBuilder.simple.noOptionsFound')}
        />
    );
});

export default SelectEditor;
